// Community lookup for the shell: GET /api/communities/{slug} (S2 owns the endpoint and may add fields).
// A renamed community answers 404 with fields.redirectSlug; we follow it with navigate(..., replace).
import { useQuery } from '@tanstack/react-query'
import { useEffect } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router'
import { api, isApiError } from './api'
import { t } from './i18n'
import { isCommunitySlug } from './reserved'
import type { MemberRole, MemberStatus } from './auth'
import type { Mute } from './mute'

export type CommunityTabKey = 'community' | 'classroom' | 'calendar' | 'members' | 'map' | 'leaderboards' | 'about'
export type OptionalTabKey = 'classroom' | 'calendar' | 'members' | 'map' | 'leaderboards'

export type Community = {
  id: string
  slug: string
  name: string
  description: string | null
  iconUrl: string | null
  coverUrl: string | null
  faviconUrl?: string | null
  brandColor: string | null
  privacy: 'public' | 'private'
  isHidden?: boolean
  memberCount: number
  onlineCount?: number
  adminCount?: number
  tabs: Partial<Record<OptionalTabKey, boolean>>
  pricingModel?: 'free' | 'subscription' | 'freemium' | 'tiers' | 'one_time'
  currency?: string
  /** The viewer's relation, when the endpoint includes it (falls back to me.communities). `mute`: FAM-9, the viewer
   * is muted here (composers, the calendar and chat show the notice up front). */
  viewer?: { role: MemberRole | null; status: MemberStatus | null; canViewContent?: boolean; mute?: Mute | null } | null
}

export const communityKey = (slug: string) => ['community', slug.toLowerCase()] as const

export const COMMUNITY_TABS: ReadonlyArray<{ key: CommunityTabKey; label: string; path: string }> = [
  { key: 'community', label: 'Community', path: '' },
  { key: 'classroom', label: 'Classroom', path: '/classroom' },
  { key: 'calendar', label: 'Calendar', path: '/calendar' },
  { key: 'members', label: 'Members', path: '/members' },
  { key: 'map', label: 'Map', path: '/map' },
  { key: 'leaderboards', label: 'Leaderboards', path: '/leaderboards' },
  { key: 'about', label: 'About', path: '/about' },
]

/** The translated name of a community tab (literal t() calls, so every name reaches the translators). */
export function tabLabel(key: CommunityTabKey): string {
  switch (key) {
    case 'community':
      return t('Community')
    case 'classroom':
      return t('Classroom')
    case 'calendar':
      return t('Calendar')
    case 'members':
      return t('Members')
    case 'map':
      return t('Map')
    case 'leaderboards':
      return t('Leaderboards')
    case 'about':
      return t('About')
  }
}

/** Visible tabs for a community: optional tabs hidden in settings drop out; About never hides. */
export function visibleTabs(community: Pick<Community, 'tabs'> | null | undefined) {
  return COMMUNITY_TABS.filter((tab) => {
    if (tab.key === 'community' || tab.key === 'about') return true
    return community?.tabs?.[tab.key as OptionalTabKey] !== false
  })
}

/** "/<slug>" plus an optional sub path ("/classroom"). */
export function communityPath(slug: string, sub = ''): string {
  return `/${slug}${sub}`
}

/** Path with the first segment swapped for a new slug (used after a rename). */
export function swapSlug(pathname: string, newSlug: string): string {
  const parts = pathname.split('/')
  if (parts.length < 2) return `/${newSlug}`
  parts[1] = newSlug
  return parts.join('/')
}

export function useCommunitySlug(): string | undefined {
  return useParams().slug
}

/**
 * The community for a slug (cached under ["community", slug]). The shell's CommunityLayout passes
 * followRedirects so a renamed slug (404 + fields.redirectSlug) or odd casing is replaced in the URL once.
 */
export function useCommunity(slug: string | undefined, opts: { followRedirects?: boolean } = {}) {
  const follow = !!opts.followRedirects
  const navigate = useNavigate()
  const location = useLocation()
  const query = useQuery({
    queryKey: communityKey(slug ?? ''),
    queryFn: () => api.get<Community>(`/api/communities/${encodeURIComponent(slug ?? '')}`),
    enabled: !!slug,
    staleTime: 60_000,
  })
  const redirectSlug = isApiError(query.error) && query.error.status === 404 ? query.error.field('redirectSlug') : undefined
  useEffect(() => {
    if (!follow || !redirectSlug || !slug || redirectSlug.toLowerCase() === slug.toLowerCase()) return
    navigate(swapSlug(location.pathname, redirectSlug) + location.search + location.hash, {
      replace: true,
      state: location.state,
    })
  }, [follow, redirectSlug, slug, navigate, location.pathname, location.search, location.hash, location.state])
  // Canonical casing: /MG-AI becomes /mg-ai once the community loads.
  const canonical = query.data?.slug
  useEffect(() => {
    if (!follow || !canonical || !slug || canonical === slug || canonical.toLowerCase() !== slug.toLowerCase()) return
    if (location.pathname.split('/')[1] !== slug) return
    navigate(swapSlug(location.pathname, canonical) + location.search + location.hash, { replace: true, state: location.state })
  }, [follow, canonical, slug, navigate, location.pathname, location.search, location.hash, location.state])
  const notFound = isApiError(query.error) && query.error.status === 404 && !redirectSlug
  return { ...query, community: query.data ?? null, notFound, redirecting: !!redirectSlug }
}

export const LAST_COMMUNITY_KEY = 'mgs-last-community'

/** Remembers the last community visited (bottom nav Home, members landing on "/"). */
export function rememberCommunity(slug: string): void {
  try {
    localStorage.setItem(LAST_COMMUNITY_KEY, slug)
  } catch {
    /* private mode */
  }
}

export function lastCommunity(): string | null {
  try {
    return localStorage.getItem(LAST_COMMUNITY_KEY)
  } catch {
    return null
  }
}

/** The community slug of the current URL (first path segment when it can be a community), else null. */
export function communitySlugFromPath(pathname: string): string | null {
  const first = pathname.split('/')[1] ?? ''
  return isCommunitySlug(first) ? first : null
}

/** "prompt-lab" reads "Prompt lab" (shown while the community cannot be loaded). */
export function humanizeSlug(slug: string): string {
  const words = slug.replace(/[-_]+/g, ' ').trim()
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : slug
}

const PAGEVIEW_OFF_KEY = 'mgs-pv-off'

/**
 * Counts a member's active day in a community (ANLY-003 engagement cohorts, member activity charts):
 * POST /api/communities/{slug}/pageview at most once per community per day per browser session.
 */
export function usePageviewPing(slug: string | undefined, active: boolean): void {
  useEffect(() => {
    if (!slug || !active) return
    const key = `mgs-pv:${slug}:${new Date().toISOString().slice(0, 10)}`
    try {
      if (sessionStorage.getItem(key) || sessionStorage.getItem(PAGEVIEW_OFF_KEY)) return
      sessionStorage.setItem(key, '1')
    } catch {
      /* storage blocked: the server dedupes per day anyway */
    }
    api.post(`/api/communities/${slug}/pageview`).catch((e: unknown) => {
      // Suspended accounts are read only (403 account_suspended): stop pinging for this session (Q-ACCT-11).
      if (isApiError(e) && e.code === 'account_suspended') {
        try {
          sessionStorage.setItem(PAGEVIEW_OFF_KEY, '1')
        } catch {
          /* ignore */
        }
      }
    })
  }, [slug, active])
}
